import { codeFromCodeableConcept } from '@seen/core/codeableConcept'
import {
  BLOOD_PRESSURE_CODING,
  PAIN_SEVERITY_CODING,
} from '@seen/core/models/loinc'
import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react'
import type { JSX } from 'react'
import { match } from 'ts-pattern'
import { ObservationDisplayClickable } from '#src/components/rich-text-editor/ObservationNode/ObservationDisplayClickable/ObservationDisplayClickable.tsx'
import { ObservationNodeBloodPressure } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeBloodPressure.tsx'
import { ObservationNodeChoiceInput } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeChoiceInput.tsx'
import { ObservationNodeNumber } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeNumber.tsx'
import { ObservationNodePain } from '#src/components/rich-text-editor/ObservationNode/ObservationNodePain.tsx'
import { ObservationNodeTextInput } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeTextInput.tsx'
import { ObservationWrapper } from '#src/components/rich-text-editor/ObservationNode/ObservationWrapper.tsx'
import type {
  ObservationNodeAttributes,
  ObservationNodeOptions,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useOptionalParticipantReference } from '#src/contexts/index.ts'

export function ObservationNodeView(props: NodeViewProps): JSX.Element {
  return (
    <NodeViewWrapper className="inline-flex text-sm">
      <ObservationNodeViewInner {...props} />
    </NodeViewWrapper>
  )
}

// TODO: Handle the case where there isn't a participant

function ObservationNodeViewInner(props: NodeViewProps): JSX.Element {
  const attributes = props.node.attrs as ObservationNodeAttributes
  const { scaleType, reference } = attributes

  const extensionOptions = props.extension.options as ObservationNodeOptions

  const currentParticipant = useOptionalParticipantReference()
  const participant = extensionOptions.participant ?? currentParticipant
  const onReferencePersisted = extensionOptions.onReferencePersisted
  // A signed note locks its editor; the embedded observations follow it.
  const disabled = !props.editor.isEditable

  if (attributes.code === codeFromCodeableConcept(BLOOD_PRESSURE_CODING)) {
    return (
      <ObservationNodeBloodPressure
        attributes={attributes}
        updateAttributes={props.updateAttributes}
        participant={participant}
        onReferencePersisted={onReferencePersisted}
        disabled={disabled}
      />
    )
  }

  if (attributes.code === codeFromCodeableConcept(PAIN_SEVERITY_CODING)) {
    return (
      <ObservationNodePain
        attributes={attributes}
        updateAttributes={props.updateAttributes}
        participant={participant}
        onReferencePersisted={onReferencePersisted}
        disabled={disabled}
      />
    )
  }

  if (scaleType === 'Qn') {
    return (
      <ObservationNodeNumber
        attributes={attributes}
        updateAttributes={props.updateAttributes}
        participant={participant}
        onReferencePersisted={onReferencePersisted}
        disabled={disabled}
      />
    )
  }

  if (reference) {
    return <ObservationDisplayClickable {...props} />
  }

  return (
    match(scaleType)
      .with('Ord', 'Nom', () => <ObservationNodeChoiceInput {...props} />)
      .with('Nar', () => <ObservationNodeTextInput {...props} />)
      // A kind of question this version does not know (saved by a newer one)
      // shows its label, and is not answered here.
      .otherwise(() => (
        <ObservationWrapper className="text-muted-foreground">
          <span title="This question can't be answered in this version of Orbit">
            {attributes.codingDisplay || attributes.display}: not available here
          </span>
        </ObservationWrapper>
      ))
  )
}
